import { PatchOperation } from "@medplum/core";
import { ChargeItem, Coding } from "@medplum/fhirtypes";

import { ChargeItemResourceStatus } from "@/billing/components/ServicesTab/constants/services";
import { BillingServiceTag, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";

/**
 * The status codes this module owns on `meta.tag`. Archive lives in the same CodeSystem but is
 * orthogonal to billed/unbilled, so it is deliberately absent here: clearing is scoped to these
 * codes, the way `BILLING_STATEMENT_LIFECYCLE_TAGS` scopes clearing for statements.
 */
const CHARGE_ITEM_STATUS_TAG_CODES: readonly string[] = [
  BillingServiceTag.Unbilled,
  BillingServiceTag.Billed,
];

const STATUS_TAG_DISPLAY: Record<string, string> = {
  [BillingServiceTag.Unbilled]: "Unbilled",
  [BillingServiceTag.Billed]: "Billed",
};

/**
 * Whether a `meta.tag` entry is one of the billed/unbilled mirror codes.
 *
 * @param tag The coding to test, as it appears on `ChargeItem.meta.tag`.
 * @returns True when the coding is a status mirror owned by this module.
 */
export function isChargeItemStatusTag(tag: { system?: string; code?: string }): boolean {
  return (
    tag.system === QuiltedHealthCodeSystems.BillingServiceTags &&
    tag.code !== undefined &&
    CHARGE_ITEM_STATUS_TAG_CODES.includes(tag.code)
  );
}

/**
 * Maps `ChargeItem.status` to the mirror tag code that makes it searchable.
 *
 * `ChargeItem.status` is not a FHIR R4 search parameter, so the Services tab cannot filter Unbilled
 * from Billed server-side without a mirror on an indexed field. Any status outside billable/billed
 * has no mirror and simply carries none.
 *
 * @param status The value of `ChargeItem.status`.
 * @returns The matching `BillingServiceTag` code, or undefined when the status has no mirror.
 */
export function toChargeItemStatusTagCode(status: string | undefined): string | undefined {
  if (status === ChargeItemResourceStatus.Billable) return BillingServiceTag.Unbilled;
  if (status === ChargeItemResourceStatus.Billed) return BillingServiceTag.Billed;
  return undefined;
}

/**
 * Returns a shallow copy of the ChargeItem whose `meta.tag` carries exactly one status mirror
 * matching its own `ChargeItem.status`. Tags from another system, and the archive tag, are left
 * untouched, so archiving and billing stay independent of each other.
 *
 * A status with no mirror on a charge with no other tags leaves no `meta.tag` at all, and no `meta`
 * when nothing else is in it, because FHIR JSON does not allow empty arrays or objects.
 *
 * Call this on every path that writes `ChargeItem.status`, so the mirror cannot drift from the
 * field it mirrors.
 *
 * @param chargeItem The ChargeItem about to be written.
 * @returns A copy with the status mirror tag applied.
 */
export function applyChargeItemStatusTag(chargeItem: ChargeItem): ChargeItem {
  const { meta: currentMeta, ...withoutMeta } = chargeItem;
  const { tag: currentTags, ...otherMeta } = currentMeta ?? {};
  const preservedTags = currentTags?.filter((tag) => !isChargeItemStatusTag(tag)) ?? [];
  const statusCode = toChargeItemStatusTagCode(chargeItem.status);

  const tag = statusCode
    ? [
        ...preservedTags,
        {
          system: QuiltedHealthCodeSystems.BillingServiceTags,
          code: statusCode,
          display: STATUS_TAG_DISPLAY[statusCode],
        },
      ]
    : preservedTags;

  const meta = tag.length ? { ...otherMeta, tag } : otherMeta;

  return Object.keys(meta).length ? { ...withoutMeta, meta } : withoutMeta;
}

/**
 * The patch that moves a ChargeItem to a new status and keeps its status mirror in step.
 *
 * For writers that change only the status, and patch it so nothing else on the charge is caught up
 * in the change. The tag is written only when the mirror actually changes, so a move between two
 * statuses without a mirror leaves the tag list alone.
 *
 * Every patch is guarded on the charge's `versionId`, including one that only replaces the status.
 * Whether the mirror needs writing is decided from the charge as last read, so a write that changed
 * the tag since then would otherwise leave the new status next to a mirror for the old one.
 *
 * @param chargeItem The charge as last read, which the tag list is computed from.
 * @param status The status the charge moves to.
 * @returns The operations to pass to `patchResource`.
 */
export function buildChargeItemStatusPatch(
  chargeItem: ChargeItem,
  status: ChargeItem["status"],
): PatchOperation[] {
  const statusOperation: PatchOperation = { op: "replace", path: "/status", value: status };

  const currentStatusCodes = (chargeItem.meta?.tag ?? []).reduce<string[]>((codes, tag) => {
    if (tag.code && isChargeItemStatusTag(tag)) codes.push(tag.code);
    return codes;
  }, []);
  const nextStatusCode = toChargeItemStatusTagCode(status);
  const isMirrorInStep = nextStatusCode
    ? currentStatusCodes.length === 1 && currentStatusCodes[0] === nextStatusCode
    : currentStatusCodes.length === 0;

  const versionId = chargeItem.meta?.versionId;
  const versionTest: PatchOperation[] = versionId
    ? [{ op: "test", path: "/meta/versionId", value: versionId }]
    : [];

  if (isMirrorInStep) return [...versionTest, statusOperation];

  const nextTags = applyChargeItemStatusTag({ ...chargeItem, status }).meta?.tag ?? [];

  return [...versionTest, statusOperation, toTagOperation(chargeItem, nextTags)];
}

/** Writes the whole tag list, creating `meta` when the charge has none and removing an empty list. */
function toTagOperation(chargeItem: ChargeItem, nextTags: Coding[]): PatchOperation {
  if (!chargeItem.meta) return { op: "add", path: "/meta", value: { tag: nextTags } };
  if (!nextTags.length) return { op: "remove", path: "/meta/tag" };
  return { op: "add", path: "/meta/tag", value: nextTags };
}
